import React, { useEffect, useRef, useState } from 'react'
import { Grid, Typography } from '@mui/material'
import CommonModal from './CommonModal'
import './AddEditInterfaceModal.css'

type FlowStatus = | 'IDLE' | 'INITIATED' | 'IN_PROGRESS' | 'COMPLETED'| 'FAILED' | 'CANCELLED'

type Step = {
  label: string
  status: FlowStatus
}

interface Props {
  open: boolean
  paymentStatus: FlowStatus
  onClose: () => void,
  topUpStatus: any
  transactionToken: any
}

const TOTAL_TIME = 300 // 5 minutes

const PaymentStatusModal: React.FC<Props> = ({
  open,
  paymentStatus,
  onClose,
  topUpStatus,
  transactionToken
}) => {
  const effectiveTopUpStatus: FlowStatus =
  paymentStatus === 'COMPLETED'
    ? topUpStatus
    : paymentStatus === 'FAILED'
    ? 'FAILED'
    : paymentStatus === 'CANCELLED'
    ? 'CANCELLED'
    : paymentStatus === 'INITIATED'
    ? 'INITIATED'
    : 'IDLE'

  const [secondsLeft, setSecondsLeft] = useState(TOTAL_TIME)
  const intervalRef = useRef<any>(null)
  const isInProgress = paymentStatus === 'IN_PROGRESS'
  const isFailed = paymentStatus === 'FAILED'
  const isSuccess =
    paymentStatus === 'COMPLETED' && topUpStatus === 'COMPLETED'

  useEffect(() => {
    if (!open || !isInProgress) return

    setSecondsLeft(TOTAL_TIME)

    intervalRef.current = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) {
          clearInterval(intervalRef.current)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(intervalRef.current)
  }, [open, isInProgress])

  const progressPercent =
    ((TOTAL_TIME - secondsLeft) / TOTAL_TIME) * 100

  const formatTime = (sec: number) =>
    `${Math.floor(sec / 60)}:${(sec % 60)
      .toString()
      .padStart(2, '0')}`

const steps: Step[] = [
  { label: 'Payment Initiated', status: 'COMPLETED' },
  { label: 'Payment', status: paymentStatus },
  { label: 'Top-Up', status: effectiveTopUpStatus }
]

const statusTitle =
  paymentStatus === 'CANCELLED'
    ? 'Payment Cancelled'
    : paymentStatus === 'FAILED'
    ? 'Payment Failed'
    : paymentStatus === 'COMPLETED'
    ? 'Payment Successful'
    : paymentStatus === 'INITIATED'
    ? 'Payment Initiated'
    : paymentStatus === 'IN_PROGRESS'
    ? 'Payment in Progress'
    : ''


const statusSubtitle =
  effectiveTopUpStatus === 'CANCELLED'
    ? 'Top-Up Cancelled'
    : effectiveTopUpStatus === 'FAILED'
    ? 'Top-Up Failed'
    : effectiveTopUpStatus === 'COMPLETED'
    ? 'Top-Up Successful'
    : effectiveTopUpStatus === 'INITIATED'
    ? 'Top-Up Initiated'
    : effectiveTopUpStatus === 'IN_PROGRESS'
    ? 'Top-Up in Progress'
    : ''

  const getStepClass = (status: FlowStatus) =>
  status === 'COMPLETED'
    ? 'completed'
    : status === 'FAILED' || status === 'CANCELLED'
    ? 'failed'
    : status === 'IN_PROGRESS'
    ? 'active'
    : status === 'INITIATED'
    ? 'initiated'
    : ''

    const getCircleClass = (status: FlowStatus) =>
  status === 'COMPLETED'
    ? 'success'
    : status === 'FAILED' || status === 'CANCELLED'
    ? 'failed'
    : status === 'IN_PROGRESS'
    ? 'loading'
    : status === 'INITIATED'
    ? 'initiated'
    : ''

 const getCircleContent = (status: FlowStatus) =>
  status === 'COMPLETED'
    ? '✓'
    : status === 'FAILED' || status === 'CANCELLED'
    ? '!'
    : ''

  return (
    <CommonModal
      open={open}
      onClose={isInProgress ? () => { } : onClose}
      onSave={onClose}
      title="Payment Status"
      saveText={isInProgress ? '' : 'Close'}
      showCancel={false}
      disableSave={isInProgress}
      showOnlineOffline={false}
      showSyncStatus={false}
    >
      <Grid container className="dpo-topup-payment-wrapper">

  
          <div>
            <Typography
              variant="h6"
              fontWeight={600}
              color={
                isFailed
                  ? 'error'
                  : isSuccess
                    ? 'success.main'
                    : 'inherit'
              }
            >
              {statusTitle}
            </Typography>

            <Typography className="dpo-topup-sub-text">
              {statusSubtitle}
            </Typography>

            <Typography className="dpo-topup-sub-text">
              Transaction Token: {transactionToken}
            </Typography>

            {isFailed && <Typography className="dpo-topup-sub-text"> Please contact admin for further details. </Typography>}

            {isInProgress && (
              <>
                <div className="dpo-topup-progress-bar-container">
                  <div
                    className="progress-bar"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>

                <Typography className="dpo-topup-timer-text">
                  Time remaining: {formatTime(secondsLeft)}
                </Typography>
              </>
            )}
          </div>

        <div className="dpo-topup-custom-stepper">
          {steps.map((step, index) => (
            <React.Fragment key={step.label}>
              <div className={`dpo-topup-step ${getStepClass(step.status)}`}>
                <div className={`dpo-topup-circle ${getCircleClass(step.status)}`}>
                  {getCircleContent(step.status)}
                </div>
                <span>{step.label}</span>
              </div>

              {index < steps.length - 1 && (
                <div
                  className={`dpo-topup-line ${steps[index + 1].status === 'COMPLETED'
                      ? 'completed'
                      : ''
                    }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </Grid>
    </CommonModal>
  )
}


export default PaymentStatusModal